import { Controller } from "@hotwired/stimulus";
import * as Sentry from "@sentry/browser";

// A kitchen tablet sits on the orders page for a whole
// service, so the memory of what has already rung is
// capped instead of growing with the night's orders.
const REMEMBERED_ORDERS = 50;

// 52 bytes: an 8 bit mono WAV holding 1 ms of silence.
//
// THE PROBE MUST NOT BE MUTED, which is why this exists
// at all. Browsers allow muted media to autoplay
// without a gesture, so `audio.muted = true` before
// `play()` resolves in exactly the state the real ring
// would be refused in, and would report the sound
// healthy on the one tablet it is broken on. Silent
// CONTENT is not muted playback: this plays at full
// volume through the same gate the order sound goes
// through, and makes no noise.
const SILENT_CLIP =
  "data:audio/wav;base64,UklGRiwAAABXQVZFZm10IBAAAAAB" +
  "AAEAQB8AAEAfAAABAAgAZGF0YQgAAACAgICAgICAgA==";

// The two names `HTMLMediaElement.play()` rejects with
// that are NOT an autoplay refusal: per the HTML spec it
// rejects with exactly three, and the third,
// `NotAllowedError`, is the one this whole feature is
// built around. `NotSupportedError` is a source or
// decode failure and `AbortError` is a play cut short by
// a pause or a reload. Neither is anything a merchant
// can do something about, and the notice's copy would
// send them chasing a setting that is not the problem.
const MEDIA_FAILURES = ["NotSupportedError", "AbortError"];

// EVERYTHING ELSE COUNTS AS BLOCKED, deliberately, and
// this is the asymmetry the feature turns on. Narrowing
// to the one name we know reintroduces the worst failure
// from the other side: a browser refusing autoplay under
// a name we have not seen would leave the merchant in
// silence with no notice, believing they are covered,
// which is invisible to us and costs them orders. A
// wrong notice costs one wasted tap. So an unknown or
// nameless rejection fails toward telling them.
function refusedByBrowser(error) {
  return !MEDIA_FAILURES.includes(error?.name);
}

// CAL-501. Rings the merchant's kitchen when an order
// becomes real. The server decides WHEN (see
// `RestaurantOrder#broadcast_new_order_sound`, which
// hangs off the same two seams as the cart lifecycle)
// and broadcasts a `new_order_sound` stream action on a
// stream dedicated to this signal; the action in
// `shared/shared.js` turns it into the
// `new-order-sound:arrived` DOM event this listens for.
//
// AUTOPLAY IS THE PRIMARY CASE, NOT AN EDGE. A browser
// refuses `play()` until the document has seen a user
// gesture, and a tablet propped up in a kitchen with no
// touch since it loaded is exactly that state. A
// merchant who believes the sound is covering them and
// is not is worse off than one with no sound at all, so
// this probes BEFORE any order arrives, says so in the
// page when it is blocked, and offers one tap that
// fixes it. The same notice is the fallback if a play
// is refused at the moment an order lands.
//
// Nothing here replays orders that were already on the
// page: the broadcast fires once, at the moment the
// order becomes real, and a tab that was not open then
// never hears about it.
export default class extends Controller {
  static targets = ["blocked"];
  static values = { url: String };

  connect() {
    this.rung = new Set();
    this.rungOrder = [];
    this.retried = false;
    this.attempt = 0;

    this.audio = new Audio(this.urlValue);
    this.audio.preload = "auto";

    this.probe();
  }

  // `new-order-sound:arrived@document`
  ring(event) {
    const orderId = event?.detail?.orderId;

    // Remembered BEFORE the attempt, not after it. A
    // repeated delivery has to be silent whether or not
    // the first one was allowed to play, and a refused
    // ring already has the notice to fall back on.
    if (!orderId || this.rung.has(orderId)) return;
    this.remember(orderId);

    this.sound();
  }

  // The notice's own button. A real gesture, so this
  // both unlocks the element for the rest of the
  // session and lets the merchant hear what they are
  // listening for.
  enable(event) {
    event.preventDefault();

    this.sound();
  }

  // Any gesture anywhere in the page. Once the document
  // has been touched the block is gone, so the notice
  // should leave on its own rather than wait to be
  // pressed.
  retry() {
    if (this.retried) return;

    this.retried = true;
    this.probe();
  }

  sound() {
    this.audio.currentTime = 0;

    // Stamped when the attempt STARTS, not when it
    // settles: a probe spanning this line predates the
    // real play whatever order the two land in.
    this.attempt += 1;

    const started = this.audio.play();
    if (!started) return;

    started
      .then(() => this.reveal(false))
      .catch((error) => this.failed(error, "the order sound"));
  }

  // ONLY A REAL PLAY MAY DECIDE THE NOTICE ONCE ONE HAS
  // STARTED. The probe is a prediction of what `sound()`
  // will do; `sound()` is what the merchant actually
  // hears, so a probe result from before the latest real
  // attempt is discarded rather than allowed to land on
  // top of it.
  //
  // Both orders are closed because one of them is
  // silent. `pointerdown` fires `retry()` before `click`
  // fires `enable()`, so a probe is routinely in flight
  // while a real play runs, and whichever settled last
  // used to win. In one direction that HIDES the notice
  // while the sound is genuinely blocked, and a merchant
  // who believes they are covered and is not is the
  // exact outcome this affordance exists to prevent. The
  // other only re-shows a notice on a browser that
  // works, which is merely annoying, but it is closed
  // too so the state is never an accident of timing.
  //
  // A generation counter rather than a "a real play has
  // happened" latch, because a probe STARTED after a
  // real attempt settled is not stale: that is the
  // passive dismiss, and a merchant who touched the page
  // should still see the notice leave on its own.
  async probe() {
    const attempt = this.attempt;
    const clip = new Audio(SILENT_CLIP);

    try {
      await clip.play();
      clip.pause();
      clip.currentTime = 0;

      if (attempt !== this.attempt) return;
      this.reveal(false);
    } catch (error) {
      if (attempt !== this.attempt) return;
      this.failed(error, "the autoplay probe");
    }
  }

  // Shared by the probe and a real ring so the two
  // cannot drift apart on what counts as a refusal.
  failed(error, what) {
    if (refusedByBrowser(error)) {
      this.reveal(true);
      return;
    }

    // The sound will never play for this merchant and no
    // tap of theirs will change that, so the only useful
    // place for it is where we can see it.
    // `captureConsoleIntegration` is NOT enabled in
    // `shared/sentry.js`, so the console line alone
    // reaches nobody.
    console.error(`new order sound: ${what} failed`, error);
    Sentry.captureException(error);
  }

  reveal(blocked) {
    if (!this.hasBlockedTarget) return;

    this.blockedTarget.classList.toggle("hidden", !blocked);
  }

  remember(orderId) {
    this.rung.add(orderId);
    this.rungOrder.push(orderId);

    while (this.rungOrder.length > REMEMBERED_ORDERS) {
      this.rung.delete(this.rungOrder.shift());
    }
  }
}
